import Raven from 'raven-js';
import I18n from 'I18n';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { fromMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { usePropertyValidationRules } from '../v2/hooks/usePropertyValidationRules';
const RULE_TYPES = {
  AFTER_DURATION: 'AFTER_DURATION',
  AFTER_DATETIME_DURATION: 'AFTER_DATETIME_DURATION',
  BEFORE_DURATION: 'BEFORE_DURATION',
  BEFORE_DATETIME_DURATION: 'BEFORE_DATETIME_DURATION',
  START_DATE: 'START_DATE',
  START_DATETIME: 'START_DATETIME',
  END_DATE: 'END_DATE',
  END_DATETIME: 'END_DATETIME',
  DAYS_OF_WEEK: 'DAYS_OF_WEEK'
};
const DAYS_OF_WEEK = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 0
};
const getDisabledDaysForMonth = (date, ruleArguments) => {
  const daysToAllow = ruleArguments.reduce((acc, day) => {
    acc[DAYS_OF_WEEK[day]] = true;
    return acc;
  }, {});
  const datesToDisable = [];
  const totalDaysInMonth = date.daysInMonth();
  for (let i = 1; i <= totalDaysInMonth; i++) {
    const dayOfMonth = I18n.moment().year(date.year()).month(date.month()).date(i);
    if (!Object.prototype.hasOwnProperty.call(daysToAllow, dayOfMonth.day())) {
      datesToDisable.push(fromMoment(dayOfMonth));
    }
  }

  // Disable days for week before and after the month since they're visible on the calendar
  for (let i = 1; i <= 7; i++) {
    const dayBeforeMonth = I18n.moment().year(date.year()).month(date.month()).date(1).subtract(i, 'days');
    if (!Object.prototype.hasOwnProperty.call(daysToAllow, dayBeforeMonth.day())) {
      datesToDisable.push(fromMoment(dayBeforeMonth));
    }
    const dayAfterMonth = I18n.moment().year(date.year()).month(date.month()).date(totalDaysInMonth).add(i, 'days');
    if (!Object.prototype.hasOwnProperty.call(daysToAllow, dayAfterMonth.day())) {
      datesToDisable.push(fromMoment(dayAfterMonth));
    }
  }
  return datesToDisable;
};
const useGetValidatedDateOptions = selectedDate => {
  const {
    data: validationRules
  } = usePropertyValidationRules();
  const [disabledValues, setDisabledValues] = useState([]);
  const daysOfWeekRule = useMemo(() => validationRules === null || validationRules === void 0 ? void 0 : validationRules.find(({
    ruleType
  }) => ruleType === RULE_TYPES.DAYS_OF_WEEK), [validationRules]);
  useEffect(() => {
    if (daysOfWeekRule) {
      const date = selectedDate ? I18n.moment.portalTz(Number(selectedDate)) : I18n.moment.portalTz();
      const disabledDays = getDisabledDaysForMonth(date, daysOfWeekRule.ruleArguments);
      setDisabledValues(disabledDays);
    }
  }, [daysOfWeekRule, selectedDate]);
  const onCalendarPageChange = useCallback(event => {
    if (daysOfWeekRule) {
      const {
        year,
        month
      } = event.target.value;
      const datesToDisable = getDisabledDaysForMonth(I18n.moment.portalTz().year(year).month(month), daysOfWeekRule.ruleArguments);
      setDisabledValues(datesToDisable);
    }
  }, [daysOfWeekRule]);
  const dateRestrictions = useMemo(() => {
    const output = {};
    validationRules === null || validationRules === void 0 || validationRules.forEach(({
      ruleType,
      ruleArguments
    }) => {
      switch (ruleType) {
        case RULE_TYPES.AFTER_DURATION:
        case RULE_TYPES.AFTER_DATETIME_DURATION:
          {
            const minTime = I18n.moment.portalTz().add(1, 'day');
            if (ruleArguments[0]) {
              const days = I18n.moment.duration(ruleArguments[0]);
              minTime.add(days);
            }
            output.min = fromMoment(minTime);
            break;
          }
        case RULE_TYPES.BEFORE_DURATION:
        case RULE_TYPES.BEFORE_DATETIME_DURATION:
          {
            if (ruleArguments[0] === 'P0D') {
              output.max = fromMoment(I18n.moment.portalTz().subtract(1, 'day'));
            } else {
              const maxTime = I18n.moment.portalTz().add(1, 'day');
              const days = I18n.moment.duration(ruleArguments[0]);
              maxTime.add(days);
              output.max = fromMoment(maxTime);
            }
            break;
          }
        case RULE_TYPES.START_DATE:
        case RULE_TYPES.START_DATETIME:
          {
            output.min = fromMoment(I18n.moment.portalTz(Number(ruleArguments[0])).add(1, 'day'));
            break;
          }
        case RULE_TYPES.END_DATE:
        case RULE_TYPES.END_DATETIME:
          {
            output.max = fromMoment(I18n.moment.portalTz(Number(ruleArguments[0])).add(1, 'day'));
            break;
          }
        case RULE_TYPES.DAYS_OF_WEEK:
          {
            // Days of week rule is handled outside of this switch
            break;
          }
        default:
          {
            Raven.captureException(new Error('Date validation rule type not supported'), {
              extra: {
                ruleType,
                ruleArguments
              }
            });
            break;
          }
      }
    });
    return output;
  }, [validationRules]);
  return Object.assign({}, dateRestrictions, {
    onCalendarPageChange,
    disabledValues
  });
};
export default useGetValidatedDateOptions;